import { useEffect, useState } from "react";
import api from "../services/api";
import ProductModal from "../components/ProductModal";

const rupiah = (num) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(num || 0);

export default function Products() {
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dragInfo, setDragInfo] = useState({
    productId: null,
    fromIndex: null,
  });

  // helper untuk build URL file
  const buildFileUrl = (file) => {
    if (!file) return null;
    if (file.startsWith("http")) return file; // sudah full URL
    return `${window.location.origin}/uploads/${file}`;
  };

  /* =============================
     🔄 LOAD DATA
  ============================= */
  const loadData = async () => {
    try {
      const res = await api.get("/products");
      const products = res.data.map(p => ({
        ...p,
        images: (p.images || []).map(buildFileUrl),
        video: buildFileUrl(p.video),
      }));
      setData(products);
    } catch (err) {
      console.error("Load products error:", err);
      alert("Gagal mengambil data produk");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* =============================
     🔁 DRAG IMAGE
  ============================= */
  const onDragStart = (productId, index) => {
    setDragInfo({ productId, fromIndex: index });
  };

  const onDrop = async (productId, toIndex) => {
    if (
      dragInfo.productId !== productId ||
      dragInfo.fromIndex === toIndex
    )
      return;

    const product = data.find(p => p.id === productId);
    if (!product) return;

    const newImages = [...product.images];
    const [moved] = newImages.splice(dragInfo.fromIndex, 1);
    newImages.splice(toIndex, 0, moved);

    // update UI langsung
    setData(prev =>
      prev.map(p =>
        p.id === productId ? { ...p, images: newImages } : p
      )
    );

    try {
      await api.put(`/products/${productId}/images-order`, {
        images: newImages.map(img => img.replace(`${window.location.origin}/uploads/`, "")),
      });
    } catch (err) {
      console.error("Update image order error:", err);
      alert("Gagal menyimpan urutan gambar");
      loadData();
    }

    setDragInfo({ productId: null, fromIndex: null });
  };

  /* =============================
     ❌ DELETE PRODUCT (FIX 404)
  ============================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus produk ini?")) return;

    try {
      await api.delete(`/products/${id}`);
      loadData();
    } catch (err) {
      console.error("Delete product error:", err);

      if (err.response?.status === 404) {
        alert("Produk tidak ditemukan (404)");
      } else if (err.response?.status === 401) {
        alert("Unauthorized, silakan login ulang");
      } else {
        alert("Gagal menghapus produk");
      }
    }
  };
const formatPriceRange = (min, max) => {
  if (min === max) return rupiah(min);
  return `${rupiah(min)} – ${rupiah(max)}`;
};

  return (
    <>
      <div className="d-flex justify-content-between mb-3">
        <h4>📦 Produk</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelected(null);
            setShow(true);
          }}
        >
          ➕ Tambah Produk
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>Nama</th>
              <th>Kategori</th>
              <th>Harga</th>
              <th>Varian</th>
              <th>Stok</th>
              <th>Images <small>(drag)</small></th>
              <th>Video</th>
              <th width="120">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {data.map(p => (
              <tr key={p.id}>
                <td><strong>{p.nama_produk}</strong></td>
                <td>{p.category?.name || "-"}</td>
                <td>{formatPriceRange(p.min_price, p.max_price)}</td>


                {/* Varian */}
                <td>
                  {p.variants?.map(v => (
                    <div key={v.id}>
                      <small>{v.nama_varian}</small>
                    </div>
                  ))}
                </td>

                {/* Stok */}
                <td>
                  {p.variants?.map(v => (
                    <div key={v.id}>
                      <small>{v.stok}</small>
                    </div>
                  ))}
                </td>

                {/* Images Drag */}
                <td>
                  <div className="d-flex flex-wrap gap-1">
                    {p.images?.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={p.nama_produk}
                        draggable
                        onDragStart={() => onDragStart(p.id, i)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => onDrop(p.id, i)}
                        title={
                          i === 0
                            ? "Thumbnail utama"
                            : "Drag untuk ubah urutan"
                        }
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 4,
                          cursor: "grab",
                          border:
                            i === 0
                              ? "2px solid #0d6efd"
                              : "1px solid #ccc",
                        }}
                      />
                    ))}
                  </div>
                </td>

                {/* Video */}
                <td>
                  {p.video ? (
                    <video width="120" controls>
                      <source
                        src={p.video}
                        type="video/mp4"
                      />
                    </video>
                  ) : "-"}
                </td>

                {/* Action */}
                <td className="d-flex gap-1">
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => {
                      setSelected(p);
                      setShow(true);
                    }}
                  >
                    ✏️
                  </button>

                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(p.id)}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data.length === 0 && (
          <div className="text-center text-muted mt-2">
            Belum ada produk
          </div>
        )}
      </div>

      {show && (
        <ProductModal
          show={show}
          onClose={() => setShow(false)}
          onSuccess={() => {
            setShow(false);
            loadData();
          }}
          product={selected}
        />
      )}
    </>
  );
}
